import { useEffect, useMemo, useState } from 'react';
import { Card, DataTable, InlineStack, Link, Pagination, SkeletonBodyText, Text, TextField } from '@shopify/polaris';
import { formatToCurrency, getRequest } from '../../../../shared/components/functions';
import { CardHeading } from '../../../../shared/components/cards/CardHeading';
import { formatCompactCurrency, formatInteger, formatShortDate, toNumber } from './analyticsHelpers';

const PAGE_SIZE = 25;

function orderQuarter(datum) {
  const iso = String(datum || '');
  const year = Number(iso.substring(0, 4));
  const month = Number(iso.substring(5, 7));
  if (!Number.isFinite(year) || !Number.isFinite(month) || !month) return null;
  return { key: `${year}-Q${Math.ceil(month / 3)}`, label: `Q${Math.ceil(month / 3)} ${year}` };
}

// Revenue that is already sold but not yet invoiced: open sale orders of the last 24 months.
export default function OrderBacklogCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [quarterFilter, setQuarterFilter] = useState(null);
  const [sort, setSort] = useState({ column: 'backlog_incl', direction: 'descending' });
  const [page, setPage] = useState(0);

  useEffect(() => {
    async function fetchData() {
      const result = await getRequest('/api/metrics/order-backlog');
      setData(result);
      setLoading(false);
    }
    fetchData();
  }, []);

  const orders = useMemo(() => (Array.isArray(data?.orders) ? data.orders : []), [data]);

  // Open value per quarter of the order date — old quarters with value left are the suspect ones.
  const quarters = useMemo(() => {
    const byQuarter = new Map();
    for (const row of orders) {
      const quarter = orderQuarter(row.datum);
      if (!quarter) continue;
      const entry = byQuarter.get(quarter.key) || { ...quarter, backlog_incl: 0, order_count: 0 };
      entry.backlog_incl += toNumber(row.backlog_incl);
      entry.order_count += 1;
      byQuarter.set(quarter.key, entry);
    }
    return [...byQuarter.values()].sort((a, b) => a.key.localeCompare(b.key));
  }, [orders]);

  const maxQuarterBacklog = useMemo(() => Math.max(1, ...quarters.map((q) => q.backlog_incl)), [quarters]);

  const filteredOrders = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = orders.filter((row) => {
      if (quarterFilter && orderQuarter(row.datum)?.key !== quarterFilter) return false;
      if (!needle) return true;
      return [row.order_number, row.company_name, row.reference].some((field) =>
        String(field || '')
          .toLowerCase()
          .includes(needle)
      );
    });
    const factor = sort.direction === 'ascending' ? 1 : -1;
    return filtered.sort((a, b) => {
      if (sort.column === 'datum') return factor * String(a.datum || '').localeCompare(String(b.datum || ''));
      return factor * (toNumber(a[sort.column]) - toNumber(b[sort.column]));
    });
  }, [orders, query, quarterFilter, sort]);

  const pageCount = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const pageRows = filteredOrders.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);
  const filteredBacklog = filteredOrders.reduce((total, row) => total + toNumber(row.backlog_incl), 0);
  const filterActive = Boolean(query.trim() || quarterFilter);

  const sortColumns = { 2: 'datum', 4: 'order_total_incl', 5: 'invoiced_incl', 6: 'backlog_incl' };
  const handleSort = (index, direction) => {
    if (!sortColumns[index]) return;
    setSort({ column: sortColumns[index], direction });
    setPage(0);
  };

  const summary = data?.summary;
  const rows = pageRows.map((row) => [
    <Link removeUnderline url={`/sales/${row.id}`}>
      {row.order_number || row.id}
    </Link>,
    <Link removeUnderline url={`/companies/${row.company_id}`}>
      {String(row.company_name || 'Unknown').substring(0, 36)}
    </Link>,
    formatShortDate(row.datum),
    row.reference || '',
    formatToCurrency(toNumber(row.order_total_incl)),
    formatToCurrency(toNumber(row.invoiced_incl)),
    <b>{formatToCurrency(toNumber(row.backlog_incl))}</b>
  ]);

  return (
    <Card>
      <CardHeading title="Order backlog — sold but not yet invoiced" />
      <Text tone="subdued" as="p">
        Non-canceled sale orders from the last 24 months whose order value (incl. VAT) has not been fully invoiced. This is revenue that is already won and still has to hit your invoicing. Click a
        quarter to see its open orders — old quarters with value left usually hide orders that should have been closed.
      </Text>
      {loading ? (
        <div style={{ marginTop: '12px' }}>
          <SkeletonBodyText lines={8} />
        </div>
      ) : (
        <>
          <div style={{ margin: '12px 0' }}>
            <Text variant="headingLg" as="p">
              {formatToCurrency(toNumber(summary?.backlog_incl))}
            </Text>
            <Text tone="subdued" as="p" variant="bodySm">
              across {formatInteger(summary?.open_orders)} open orders
            </Text>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch', margin: '12px 0 16px', overflowX: 'auto' }}>
            {quarters.map((quarter) => {
              const selected = quarterFilter === quarter.key;
              return (
                <button
                  key={quarter.key}
                  type="button"
                  onClick={() => {
                    setQuarterFilter(selected ? null : quarter.key);
                    setPage(0);
                  }}
                  style={{
                    flex: '1 0 90px',
                    border: selected ? '2px solid rgb(46, 125, 50)' : '1px solid var(--p-color-border, #e3e3e3)',
                    borderRadius: '8px',
                    background: selected ? 'rgba(46, 125, 50, 0.08)' : 'transparent',
                    padding: '8px 6px 6px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    gap: '4px'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', height: '56px' }}>
                    <div
                      style={{
                        width: '70%',
                        height: `${Math.max(3, (quarter.backlog_incl / maxQuarterBacklog) * 56)}px`,
                        background: 'rgb(46, 125, 50)',
                        opacity: selected ? 1 : 0.55,
                        borderRadius: '3px 3px 0 0'
                      }}
                    />
                  </div>
                  <Text as="p" variant="bodySm" alignment="center" fontWeight={selected ? 'bold' : 'regular'}>
                    {quarter.label}
                  </Text>
                  <Text as="p" variant="bodySm" alignment="center" tone="subdued">
                    {formatCompactCurrency(quarter.backlog_incl)} · {formatInteger(quarter.order_count)}
                  </Text>
                </button>
              );
            })}
          </div>
          <div style={{ margin: '12px 0' }}>
            <InlineStack align="space-between" blockAlign="center" gap="200">
              <div style={{ minWidth: '260px' }}>
                <TextField
                  label="Search"
                  labelHidden
                  placeholder="Search order, customer or reference"
                  value={query}
                  onChange={(value) => {
                    setQuery(value);
                    setPage(0);
                  }}
                  clearButton
                  onClearButtonClick={() => {
                    setQuery('');
                    setPage(0);
                  }}
                  autoComplete="off"
                />
              </div>
              <InlineStack gap="300" blockAlign="center">
                {filterActive ? (
                  <Text tone="subdued" as="span" variant="bodySm">
                    filtered: {formatInteger(filteredOrders.length)} orders · {formatToCurrency(filteredBacklog)}
                  </Text>
                ) : null}
                <Pagination
                  hasPrevious={currentPage > 0}
                  hasNext={currentPage < pageCount - 1}
                  onPrevious={() => setPage(currentPage - 1)}
                  onNext={() => setPage(currentPage + 1)}
                  label={
                    <Text as="span" variant="bodySm">
                      {currentPage + 1} / {pageCount}
                    </Text>
                  }
                />
              </InlineStack>
            </InlineStack>
          </div>
          <DataTable
            verticalAlign="middle"
            increasedTableDensity
            stickyHeader
            columnContentTypes={['text', 'text', 'text', 'text', 'numeric', 'numeric', 'numeric']}
            headings={[<b>Order</b>, <b>Customer</b>, <b>Date</b>, <b>Reference</b>, <b>Order total</b>, <b>Invoiced</b>, <b>Backlog</b>]}
            sortable={[false, false, true, false, true, true, true]}
            defaultSortDirection="descending"
            initialSortColumnIndex={6}
            onSort={handleSort}
            rows={rows}
          />
        </>
      )}
    </Card>
  );
}
